import { useState, useCallback, memo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAlertas, type Alerta } from '@hooks/useAlertas.js'
import { useChecklistPendentes } from '@hooks/useChecklistPendentes.js'
import { ChecklistPendentesResumoCard } from '@components/guia/ChecklistPendentesResumoCard.js'
import { Button } from '@components/primitives/Button.js'
import { EmptyPanel } from '@components/primitives/EmptyPanel.js'
import { Badge } from '@components/primitives/Badge.js'
import { CardListSkeleton, PageHeaderSkeleton } from '@components/primitives/Skeleton.js'
import { PageShell } from '@components/layout/PageShell.js'
import { ModePageHeader } from '@components/layout/ModePageHeader.js'
import { ROUTES } from '@utils/constants.js'
import { formatDate } from '@utils/format.js'
import { requestAlertasNotificationPermission } from '@services/alertasReminder.js'
import { rotaDoAlerta } from '@services/alertasService.js'

// ── Alert type config ─────────────────────────────────────────

const TIPO_CONFIG: Record<
  Alerta['tipo'],
  { icon: string; variantBadge: 'info' | 'warning' | 'danger' | 'success'; label: string }
> = {
  info: { icon: 'i', variantBadge: 'info', label: 'Info' },
  warning: { icon: '!', variantBadge: 'warning', label: 'Atenção' },
  danger: { icon: '!!', variantBadge: 'danger', label: 'Crítico' },
  success: { icon: 'OK', variantBadge: 'success', label: 'Ok' },
}

// ── Alert card ────────────────────────────────────────────────

interface AlertaCardProps {
  alerta: Alerta
  onResolver?: () => void
  onIgnorar?: () => void
  historico?: boolean
}

const AlertaCard = memo(function AlertaCard({
  alerta,
  onResolver,
  onIgnorar,
  historico,
}: AlertaCardProps) {
  const navigate = useNavigate()
  const cfg = TIPO_CONFIG[alerta.tipo]
  const rota = rotaDoAlerta(alerta.chave)

  return (
    <div
      className={`cf3-alerta-card cf3-alerta-card--${alerta.tipo}${historico ? ' cf3-alerta-card--historico' : ''}`}
    >
      <div className='cf3-alerta-card__header'>
        <span className='cf3-alerta-card__icon' aria-hidden>
          {cfg.icon}
        </span>
        <div className='cf3-alerta-card__info'>
          <span className='cf3-alerta-card__titulo'>{alerta.titulo}</span>
          <Badge label={cfg.label} variant={cfg.variantBadge} />
        </div>
        {alerta.status && alerta.status !== 'ativo' && (
          <span className='cf3-alerta-card__status-label'>
            {alerta.status === 'resolvido' ? 'Resolvido' : 'Ignorado'}
          </span>
        )}
      </div>
      <p className='cf3-alerta-card__mensagem'>{alerta.mensagem}</p>
      <div className='cf3-alerta-card__footer'>
        <span className='cf3-alerta-card__data'>{formatDate(alerta.criadoEm)}</span>
        {!historico && (
          <div className='cf3-alerta-card__actions'>
            {rota && (
              <button type='button' className='cf3-alerta-card__btn' onClick={() => navigate(rota)}>
                Ver
              </button>
            )}
            <button
              className='cf3-alerta-card__btn cf3-alerta-card__btn--ignorar'
              onClick={onIgnorar}
              aria-label='Ignorar alerta'
            >
              Ignorar
            </button>
            <button
              className='cf3-alerta-card__btn cf3-alerta-card__btn--resolver'
              onClick={onResolver}
              aria-label='Marcar como resolvido'
            >
              Resolver
            </button>
          </div>
        )}
      </div>
    </div>
  )
})

// ── Empty state ───────────────────────────────────────────────

const EmptyState = memo(function EmptyState({
  tab,
  temChecklistPendentes,
}: {
  tab: string
  temChecklistPendentes?: boolean
}) {
  if (tab === 'ativos' && temChecklistPendentes) {
    return (
      <EmptyPanel
        className='cf3-empty-panel--compact'
        title='Nenhum alerta automático'
        description='Orçamento, notas fiscais e sequência do guia estão em dia. Veja acima os checklists da jornada que ainda precisam de atenção.'
      />
    )
  }

  return (
    <EmptyPanel
      title={tab === 'ativos' ? 'Tudo em dia' : 'Histórico vazio'}
      description={
        tab === 'ativos'
          ? 'Nenhum alerta ativo. Sua obra está em dia!'
          : 'Nenhum alerta no histórico.'
      }
    />
  )
})

// ── Main page ─────────────────────────────────────────────────

type TabId = 'ativos' | 'historico'

export function AlertasPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<TabId>(() => {
    const saved = localStorage.getItem('alertas_tab')
    return saved === 'ativos' || saved === 'historico' ? saved : 'ativos'
  })
  const { alertasAtivos, alertasHistorico, totalAtivos, isLoading, resolver, ignorar, gerar } =
    useAlertas(true)
  const {
    totalItens: checklistPendentes,
    totalPassos: passosComPendencia,
    isLoading: checklistLoading,
  } = useChecklistPendentes()

  // Persist tab state
  useEffect(() => {
    localStorage.setItem('alertas_tab', tab)
  }, [tab])

  const handleSetTab = useCallback((t: TabId) => {
    setTab(t)
  }, [])

  const handleGerar = useCallback(() => {
    gerar()
  }, [gerar])

  const handleAtivarNotificacoes = useCallback(async () => {
    await requestAlertasNotificationPermission()
  }, [])

  const handleResolver = useCallback(
    (id: number) => {
      resolver(id)
    },
    [resolver],
  )

  const handleIgnorar = useCallback(
    (id: number) => {
      ignorar(id)
    },
    [ignorar],
  )

  if (isLoading || checklistLoading) {
    return (
      <PageShell>
        <PageHeaderSkeleton label='Carregando alertas…' />
        <section className='cf3-block'>
          <CardListSkeleton count={3} label='Carregando alertas…' />
        </section>
      </PageShell>
    )
  }

  const lista = tab === 'ativos' ? alertasAtivos : alertasHistorico
  const temChecklistPendentes = checklistPendentes > 0
  const pendenciasTotais = totalAtivos + checklistPendentes

  return (
    <PageShell>
      <ModePageHeader
        title='Alertas'
        subtitle={
          pendenciasTotais > 0
            ? [
                totalAtivos > 0
                  ? `${totalAtivos} alerta${totalAtivos !== 1 ? 's' : ''} automático${totalAtivos !== 1 ? 's' : ''}`
                  : null,
                temChecklistPendentes
                  ? `${checklistPendentes} item${checklistPendentes !== 1 ? 'ns' : ''} de checklist`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ') + '.'
            : 'Avisos que o app gera: orçamento, NF, contrato e atraso. Checklists do guia aparecem à parte.'
        }
      >
        <Button
          variant='secondary'
          size='sm'
          onClick={handleGerar}
          className='cf3-section-hero__action'
        >
          Verificar agora
        </Button>
        {typeof window !== 'undefined' &&
          'Notification' in window &&
          Notification.permission === 'default' && (
            <Button variant='ghost' size='sm' onClick={handleAtivarNotificacoes}>
              Ativar lembretes
            </Button>
          )}
      </ModePageHeader>

      <div
        className='cf3-segment cf3-segment--full cf3-segment--2'
        role='tablist'
        aria-label='Filtro de alertas'
      >
        {(['ativos', 'historico'] as TabId[]).map((t) => (
          <button
            key={t}
            role='tab'
            aria-selected={tab === t}
            className={`cf3-segment__btn${tab === t ? ' cf3-segment__btn--active' : ''}`}
            onClick={() => handleSetTab(t)}
          >
            {t === 'ativos' ? `Ativos${totalAtivos > 0 ? ` (${totalAtivos})` : ''}` : 'Histórico'}
          </button>
        ))}
      </div>

      <section className='cf3-block'>
        {tab === 'ativos' && temChecklistPendentes && (
          <ChecklistPendentesResumoCard
            totalItens={checklistPendentes}
            totalPassos={passosComPendencia}
          />
        )}

        {lista.length === 0 ? (
          <EmptyState tab={tab} temChecklistPendentes={tab === 'ativos' && temChecklistPendentes} />
        ) : (
          <div className='cf3-alertas-lista'>
            {lista.map((a) => (
              <AlertaCard
                key={a.id}
                alerta={a}
                historico={tab === 'historico'}
                onResolver={() => handleResolver(a.id!)}
                onIgnorar={() => handleIgnorar(a.id!)}
              />
            ))}
          </div>
        )}
      </section>

      <div className='cf3-block__card cf3-insight'>
        <p className='cf3-insight__text'>
          Alertas o app gera sozinho. Toque em <strong>Ver</strong> para ir ao ponto. Checklists do
          guia (jornada) aparecem acima se houver item atrasado.{' '}
          <button
            type='button'
            className='cf3-insight__link'
            onClick={() => navigate(ROUTES.CHECKLIST_PENDENTES)}
          >
            abrir checklists do guia
          </button>
          . Combinado do canteiro fica em{' '}
          <button
            type='button'
            className='cf3-insight__link'
            onClick={() => navigate(ROUTES.PENDENCIAS)}
          >
            Tarefas do canteiro
          </button>
          .
        </p>
      </div>
    </PageShell>
  )
}
